import React, { memo } from 'react'
import { connect } from 'react-redux'
import { Select } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import cloneDeep from 'lodash/cloneDeep'
import PropTypes from 'prop-types'
import { FormModal } from 'transfix-ui/components/Modals'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import Typography from 'transfix-ui/core/Typography'
import {
  updateUserProfileDetails,
  updateParentUserProfileDetails,
  closeDialog,
} from 'utils/actions/user'
import { getSourceValue } from 'apps/user/components/utils'
import { roles, rolesHash, userTypesHash, userTypesGrouped } from 'apps/user/constants/userDialog'
import useUser from 'apps/user/utils/hooks/useUser'
import { TextInput } from 'components/atoms'

export const UserDetailsDialogComponent = memo(
  ({
    closeDialog: closeDialogProp,
    dialogOpen,
    error,
    showUserAccounts,
    updateParentUserProfileDetails: updateParentUserProfileDetailsProp,
    updateUserProfileDetails: updateUserProfileDetailsProp,
    updating,
    user: initialState,
    users: children,
  }) => {
    const [user, viewState, onChange, validation, validate] = useUser(
      initialState,
      dialogOpen,
      children
    )
    const userType = userTypesHash[user.userType] || {}
    const changeValue = ({ target: { id, value } }) => onChange({ [id]: value })
    const onSelect = ({ value }, id) => onChange({ [id]: value })
    const changeCompany = ({
      value: companyId = null,
      fullSelection: { name: companyName = null } = {},
    }) => onChange({ companyId, companyName, applicationId: companyId })
    const submitDisabled = updating || validation.invalid
    const userSource = user?.source ? getSourceValue(user.source) : null

    const onSubmit = () => {
      if (validate()) {
        if (showUserAccounts) {
          updateParentUserProfileDetailsProp(user)
        } else {
          updateUserProfileDetailsProp(user)
        }
      }
    }

    return (
      <FormModal
        title='Edit User Details'
        size={6}
        open={dialogOpen}
        onClose={closeDialogProp}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            disabled: updating,
            dataTest: 'user-edit-cancel',
          },
          {
            label: 'Save',
            type: 'confirm',
            action: onSubmit,
            disabled: submitDisabled,
            dataTest: 'user-edit-save',
          },
        ]}
        error={error}>
        {!showUserAccounts &&
          !!(viewState.showUserType || viewState.showRole || viewState.showCompany) && (
            <div className='pv2 row'>
              {viewState.showUserType && (
                <div className='col-xs-12 col-md-4'>
                  <Select
                    id='userType'
                    label='User Type'
                    options={userTypesGrouped[userType.type]}
                    value={userType}
                    onChange={onSelect}
                    data-test='userType'
                  />
                </div>
              )}
              {viewState.showRole && (
                <div className='col-xs-12 col-md-4'>
                  <Select
                    id='role'
                    label='Role'
                    options={roles}
                    value={rolesHash[user.role]}
                    onChange={onSelect}
                    data-test='role'
                  />
                </div>
              )}
              {viewState.showCompany && (
                <div className='col-xs-12 col-md-4'>
                  {userType.type && (
                    <Autocomplete
                      id='company'
                      label='Company'
                      value={user.companyName}
                      onChange={changeCompany}
                      type={userType.type}
                    />
                  )}
                </div>
              )}
            </div>
          )}

        <div className='pv2 row'>
          <div className='col-xs-12 col-md-4'>
            <TextInput
              id='firstName'
              data-test='firstName'
              label='First Name'
              value={user.firstName || ''}
              onChange={changeValue}
              invalid={!!validation.firstName}
              validationMessage={validation.firstName}
            />
          </div>
          <div className='col-xs-12 col-md-4'>
            <TextInput
              id='lastName'
              data-test='lastName'
              label='Last Name'
              value={user.lastName || ''}
              onChange={changeValue}
              invalid={!!validation.lastName}
              validationMessage={validation.lastName}
            />
          </div>
        </div>

        {viewState.showPhone && (
          <div className='pv2 row'>
            <div className='col-xs-12 col-md-4'>
              <PhoneInput
                name='phone'
                id='phone'
                label={`Phone${viewState.phoneRequired ? '' : ' (Optional)'}`}
                data-test='phone'
                placeholder='(555) 555-5555'
                value={user.phone || ''}
                onChange={changeValue}
                invalid={!!validation.phone}
                validationMessage={validation.phone}
                clearable={false}
              />
            </div>
            <div className='col-xs-6 col-md-2'>
              <TextInput
                id='phoneExt'
                data-test='phoneExt'
                label='Extension'
                value={user.phoneExt || ''}
                onChange={changeValue}
                type='number'
                min='0'
                placeholder='0000'
              />
            </div>
          </div>
        )}

        {viewState.showCellPhone && (
          <div className='pv2 row'>
            <div className='col-xs-12 col-md-4'>
              <PhoneInput
                name='cellPhone'
                id='cellPhone'
                label={`Cell Phone${viewState.cellPhoneRequired ? '' : ' (Optional)'}`}
                data-test='cellPhone'
                placeholder='(555) 555-5555'
                value={user.cellPhone || ''}
                onChange={changeValue}
                invalid={!!validation.cellPhone}
                validationMessage={validation.cellPhone}
                clearable={false}
              />
            </div>
          </div>
        )}

        {viewState.showEmail && (
          <div className='pv2 row'>
            <div className='col-xs-12 col-md-4'>
              <TextInput
                id='email'
                data-test='email'
                placeholder='janedoe@example.com'
                label='Email'
                value={user.email || ''}
                onChange={changeValue}
                invalid={!!validation.email}
                validationMessage={validation.email}
              />
            </div>
          </div>
        )}

        {userSource && (
          <div className='pv2 row'>
            <div className='col-xs-12 col-md-4'>
              <Typography variant='caption' color='textSecondary'>
                Created by{' '}
              </Typography>
              <Typography variant='body2'>{userSource}</Typography>
            </div>
          </div>
        )}
      </FormModal>
    )
  }
)

UserDetailsDialogComponent.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    userType: PropTypes.string,
    role: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.string,
    phoneExt: PropTypes.string,
    cellPhone: PropTypes.string,
    email: PropTypes.string,
    companyId: PropTypes.number,
    applicationId: PropTypes.number,
    companyName: PropTypes.string,
  }),
  dialogOpen: PropTypes.bool,
  showUserAccounts: PropTypes.bool,
  closeDialog: PropTypes.func,
  error: PropTypes.string,
  updateUserProfileDetails: PropTypes.func,
  updateParentUserProfileDetails: PropTypes.func,
  updating: PropTypes.bool,
}

UserDetailsDialogComponent.defaultProps = {
  user: {},
  dialogOpen: false,
  showUserAccounts: false,
  closeDialog: () => {},
  error: null,
  updateUserProfileDetails: () => {},
  updateParentUserProfileDetails: () => {},
  updating: false,
}

export const UserDetailsDialog = connect(
  ({
    childUsers: { users },
    userProfile: {
      dialogOpen,
      isDispatcher,
      isDriver,
      isOwnerOperator,
      updateError: error,
      updating,
      userProfileDetails,
    },
  }) => ({
    user: cloneDeep(
      !(isDriver || isDispatcher || isOwnerOperator)
        ? users[0] || userProfileDetails
        : userProfileDetails
    ),
    showUserAccounts: isDriver || isDispatcher || isOwnerOperator,
    updating,
    error,
    dialogOpen,
    users,
  }),
  { updateUserProfileDetails, updateParentUserProfileDetails, closeDialog }
)(UserDetailsDialogComponent)
